import type { GetIssueResponseDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssues } from '@issuemanager/client/features/issues';
import { useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type IssueMultipleSelectorProps = MultipleSelectorProps<
  GetIssueResponseDto[],
  GetIssueResponseDto
>;

export function IssueMultipleSelector(props: IssueMultipleSelectorProps) {
  const { value, onChange, enabled: _enabled, ...rest } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);

  const issuesQuery = useInfiniteIssues({
    filter: {
      searchTerm: filterByText,
    },
    enabled: filterByText.length > 0,
    usePost: true,
  });

  const options = mergePaginatedDataAndValue(
    issuesQuery.data?.pages.map((c) => c.data) ?? [],
    value ?? [],
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issuesQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => option.code}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
